import { SelectedWeatherItemType } from '@fe/weather';
import React, { useCallback } from 'react';
import { MessageType } from '../../../../services/actor-messages';
import { FilterGroupType } from '../../../../types/filter-group';
import { useEvent } from '../../../contexts/event-bus';
import WorkspaceItemContext from '../../../contexts/workspace/workspace-item-context';
import { useAppDispatch, useAppSelector } from '../../../hooks/store-hooks';
import { useFlightWorker } from '../../../hooks/use-flight-worker';
import { useSafeEffect } from '../../../hooks/use-safe-effect';
import { patchFocusedWorkspaceView } from '../../../state/store';
import { useWorkspace, useWorkspaceView } from '../hooks/use-view';

const FCA_ID = 0x65;
const DELAYS_ID = 0x60;

export function WeatherLayer() {
    const flightWorker = useFlightWorker();
    const context = React.useContext(WorkspaceItemContext);
    const dsuaSettings = useWorkspaceView((x) => x.staticData.sua);
    const showWeather = useWorkspaceView((x) => x.weather.show);
    const showConstrainsOnMap = useAppSelector((x) => x.settings.constraintsShowOnMap);
    const suaFilterGroups = useAppSelector((x) => x.filterGroups.groups[FilterGroupType.sua]);
    const airportFilterGroups = useAppSelector((x) => x.filterGroups.groups[FilterGroupType.airport]);
    const isPlayback = useAppSelector((x) => x.global.playback.active);
    // TODO: clean this up a bit
    const activeWeather = useAppSelector((x) => (isPlayback ? x.weather.playback.products : x.weather.live.products));
    const accessInfoReceived = useAppSelector((x) => !!x.weather.live.accessInfo);
    const myAirports = useAppSelector((x) => x.global.flightWorker.myAirports);
    const weather = context.weatherService;

    const view = useWorkspace(
        (x) => x.view,
        (a, b) => a.id === b.id
    );
    const dispatch = useAppDispatch();
    const focusedWorkspaceId = useAppSelector((x) => x.workspace.selectedId);

    // Toggle a single weather product (or altitude group) on the focused workspace, fired from the Command Center.
    useEvent(
        'toggle-weather-product',
        useCallback(
            (args) => {
                if (context.id !== focusedWorkspaceId) {
                    return;
                }
                const selected = weather.getSelectedProducts();
                if ('groupId' in args) {
                    const active = selected.some((p) => p.product.group === args.groupId && p.type === SelectedWeatherItemType.weather);
                    if (active) {
                        weather.removeAltitudeGroup(args.groupId);
                    } else {
                        const picked = weather.altitudePicker.pick(args.groupId);
                        weather.addProduct(picked?.id ?? args.fallbackProductId, SelectedWeatherItemType.weather);
                    }
                } else {
                    const active = selected.some((p) => p.id === args.productId && p.type === SelectedWeatherItemType.weather);
                    if (active) {
                        weather.removeProduct(args.productId, SelectedWeatherItemType.weather);
                    } else {
                        weather.addProduct(args.productId, SelectedWeatherItemType.weather);
                    }
                }
            },
            [context.id, focusedWorkspaceId, weather]
        )
    );

    useSafeEffect(() => {
        weather.setView(view);
    }, [weather, view]);

    useSafeEffect(() => {
        weather.showHideAllWeatherProducts(showWeather);
    }, [weather, showWeather]);

    useSafeEffect(() => {
        weather.onSuaSettingsChanged(dsuaSettings);
    }, [weather, dsuaSettings]);

    useSafeEffect(() => {
        weather.onMyAirportsUpdated(myAirports);
    }, [weather, myAirports]);

    useSafeEffect(() => {
        flightWorker.actor
            // This will append any iata/iata/faa ID alternative to each filter group
            .sendAsync({ type: MessageType.addAlternativeAirportIdsToFilterGroups, data: airportFilterGroups })
            .then((groups) => weather.onAirportFilterGroupsUpdated(groups));
    }, [weather, flightWorker, airportFilterGroups]);

    useSafeEffect(() => {
        weather.onSuaFilterGroupUpdated(suaFilterGroups);
    }, [weather, suaFilterGroups]);

    useSafeEffect(() => {
        weather.productsUpdated(activeWeather);
    }, [weather, activeWeather]);

    useEvent(
        'reload-all-weather',
        useCallback(() => {
            weather.refreshProducts({ force: true });
        }, [weather])
    );

    useSafeEffect(() => {
        weather.refreshProducts({ force: true });
    }, [weather, accessInfoReceived]);

    // FIXME: sync state changes to weather layer not the other way around
    useSafeEffect(() => {
        return weather.on('product.add-remove-visibility', () => {
            const list = weather.getUdViewList();
            dispatch(
                patchFocusedWorkspaceView((x) => {
                    if (x.weather.all.length === 0) {
                        x.weather.show = true;
                    }
                    x.weather.all = list.all;
                    x.weather.hidden = list.hidden;
                })
            );
        }).unsubscribe;
    }, [weather]);

    useSafeEffect(() => {
        if (showConstrainsOnMap) {
            weather.addProduct(DELAYS_ID, SelectedWeatherItemType.global);
            weather.addProduct(FCA_ID, SelectedWeatherItemType.global);
        } else {
            weather.removeProduct(DELAYS_ID, SelectedWeatherItemType.global);
            weather.removeProduct(FCA_ID, SelectedWeatherItemType.global);
        }
    }, [weather, showConstrainsOnMap]);

    return null;
}
